import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { adminApi, errMsg, startImpersonation } from "@/lib/api";
import { CURRENCIES } from "@/lib/plans";
import { StoreSettingsForm, emptyStore, storeBody } from "@/components/StoreSettingsForm";

const ONE = { businesses: "business", branches: "branch", users: "user", products: "product", batches: "batch", sales: "sale", returns: "return", credit_notes: "credit note", stores: "store" };
export const countOf = (n, one, many) => `${n} ${n === 1 ? one : many}`;
export const bulkSummary = (r, verb) => {
  const errs = [...new Set(r.results.filter((x) => !x.ok).map((x) => x.error))];
  return `${r.done} ${verb}${r.skipped ? `, ${r.skipped} skipped — ${errs.join("; ")}` : ""}`;
};
const KINDS = [["businesses", "Businesses"], ["branches", "Branches"], ["users", "Users"], ["products", "Products"], ["batches", "Batches"], ["sales", "Sales"], ["returns", "Returns"], ["credit_notes", "Credit notes"], ["stores", "Stores"], ["menu_categories", "Menu categories"], ["rest_sections", "Sections"], ["rest_tables", "Tables"], ["reservations", "Reservations"], ["rest_orders", "Restaurant orders"]];
const FIELDS = {
  businesses: [["name", "Name"], ["business_type", "Type", [["general_retail", "Retail"], ["pharmacy", "Pharmacy"], ["restaurant", "Restaurant"]]], ["country", "Country", ["PK", "AE", "OM", "IN", "OTHER"]], ["tax_number", "Tax number"], ["tax_rate", "Tax rate %"], ["tax_inclusive", "Prices include tax", "bool"], ["demo_expires_at", "Demo expires (ISO date)"], ["inventory_entitled", "Inventory add-on enabled", "bool"], ["reports_entitled", "Reports add-on enabled", "bool"], ["online_ordering_entitled", "Online Ordering add-on (restaurants)", "bool"], ["plan", "Current plan", "plans"], ["store_override", "Online store", [["plan", "By plan"], ["on", "Force on"], ["off", "Force off"]]]],
  branches: [["name", "Name"], ["code", "Receipt prefix"], ["address", "Address"], ["phone", "Phone"]],
  users: [["name", "Name"], ["username", "Username (staff)"], ["email", "Email (owner)"], ["role", "Role (staff)", ["manager", "cashier"]], ["branch_ids", "Branches (staff)", "branches"], ["is_active", "Active", "bool"]],
  products: [["name", "Name"], ["barcode", "Barcode"], ["price", "Base price"], ["tax_rate", "Tax rate % (blank = default)"], ["branch_prices", "Branch price", "perbranch"], ["stock", "Stock", "perbranch"]],
  returns: [["reason", "Reason"]],
  credit_notes: [["expires_on", "Expires on (YYYY-MM-DD)"]],
  batches: [["batch_no", "Batch no"], ["expiry", "Expiry (YYYY-MM-DD)"], ["qty", "Quantity", "int"], ["branch_id", "Branch", "branch"]],
};
export const recordName = (kind, r) => ({ returns: `return ${r.number}`, credit_notes: `credit note ${r.number}`, ...{ businesses: r.name, branches: r.name, users: r.name || r.username || r.email, products: r.name, batches: `batch ${r.batch_no}`, sales: `sale ${r.receipt_code}`, stores: `store ${r.slug}` } }[kind]);
const cols = {
  returns: (r) => [r.number, r.receipt_code, `${r.currency} ${r.refund_str}`, r.method === "cash" ? "cash" : r.credit_note?.number, r.business_name],
  credit_notes: (r) => [r.number, r.status, `${r.currency} ${r.amount_str}`, r.expires_on, r.business_name],
  businesses: (r) => [r.name, r.business_type, `${r.country} · ${r.currency}`, r.plan, (r.demo_expires_at || "").slice(0, 10)],
  branches: (r) => [r.name, r.code, r.business_name, r.address || "—"],
  users: (r) => [r.name, r.username || r.email, r.role, r.is_active === false ? "disabled" : "active", r.business_name],
  products: (r) => [r.name, r.barcode || "—", r.price, r.business_name],
  batches: (r) => [r.batch_no, r.product_name, r.expiry, r.qty, r.business_name],
  stores: (r) => [r.name, `/${r.slug}`, r.business_name, r.eligible ? "eligible" : "not eligible", `override: ${r.override || "plan"}`, `${r.orders} orders`],
  sales: (r) => [r.receipt_code, (r.created_at || "").slice(0, 16).replace("T", " "), `${r.currency} ${r.totals?.total_str ?? "—"}`, r.payment?.method || "—", r.business_name],
};

function Field({ f, value, onChange, branches, plans = [] }) {
  const [key, label, type] = f;
  const cls = "h-10 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-slate-900";
  let input;
  if (type === "bool") input = <Switch checked={!!value} onCheckedChange={onChange} data-testid={`admin-edit-${key}`} />;
  else if (Array.isArray(type)) input = <select className={cls} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={`admin-edit-${key}`}><option value="">—</option>{type.map((o) => { const [val, label] = Array.isArray(o) ? o : [o, o]; return <option key={val} value={val}>{label}</option>; })}</select>;
  else if (type === "plans") input = <select className={cls} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={`admin-edit-${key}`}><option value="demo">Demo</option>{plans.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}</select>;
  else if (type === "branch") input = <select className={cls} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={`admin-edit-${key}`}>{branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</select>;
  else if (type === "branches")
    input = (
      <div className="flex flex-wrap gap-2" data-testid={`admin-edit-${key}`}>
        {branches.map((b) => (
          <label key={b.id} className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={(value || []).includes(b.id)} onChange={(e) => onChange(e.target.checked ? [...(value || []), b.id] : (value || []).filter((x) => x !== b.id))} />{b.name}</label>
        ))}
      </div>
    );
  else if (type === "perbranch")
    input = (
      <div className="space-y-1.5">
        {branches.map((b) => (
          <div key={b.id} className="flex items-center gap-2"><span className="w-28 truncate text-xs text-slate-500">{b.name}</span>
            <input className={cls} value={value?.[b.id] ?? ""} onChange={(e) => onChange({ ...(value || {}), [b.id]: e.target.value })} data-testid={`admin-edit-${key}-${b.id}`} placeholder={key === "stock" ? "untracked" : "base price"} /></div>
        ))}
      </div>
    );
  else input = <input className={cls} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={`admin-edit-${key}`} />;
  return <label className="block space-y-1"><span className="text-xs font-semibold text-slate-600">{label}</span>{input}</label>;
}

function CustomPrices({ biz }) {
  const [plans, setPlans] = useState([]);
  const [f, setF] = useState({ plan_code: "", currency: biz.currency, price: "" });
  const [cp, setCp] = useState(biz.custom_prices || {});
  useEffect(() => { adminApi.get("/plans").then((r) => { setPlans(r.data); setF((x) => ({ ...x, plan_code: r.data[0]?.code || "" })); }); }, []);
  const save = async () => {
    try { setCp((await adminApi.put(`/businesses/${biz.id}/custom-prices`, f)).data.custom_prices); toast.success("Special price saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  const cls = "h-10 rounded-lg border border-slate-200 px-2 text-sm";
  return (
    <div className="space-y-2 rounded-xl border border-amber-200 bg-amber-50/60 p-3" data-testid="admin-custom-prices">
      <div className="text-xs font-bold uppercase tracking-wider text-amber-800">Special price for this business (blank clears)</div>
      <div className="flex flex-wrap gap-2">
        <select className={cls} value={f.plan_code} onChange={(e) => setF({ ...f, plan_code: e.target.value })} data-testid="admin-custom-plan">{plans.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}</select>
        <select className={cls} value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })} data-testid="admin-custom-currency">{CURRENCIES.map((c) => <option key={c}>{c}</option>)}</select>
        <input className={`${cls} w-28`} value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} placeholder="price/month" data-testid="admin-custom-price" />
        <button type="button" onClick={save} className="h-10 rounded-lg bg-slate-900 px-3 text-sm font-bold text-white" data-testid="admin-custom-save">Save</button>
      </div>
      <div className="font-mono text-[11px] text-slate-600" data-testid="admin-custom-list">{Object.entries(cp).map(([p, v]) => `${p}: ${Object.entries(v).map(([c, x]) => `${c} ${x}`).join(", ")}`).join(" · ") || "No special prices"}</div>
    </div>
  );
}

function SaleForm({ v, set }) {
  return (
    <div className="space-y-2">
      {v.lines.map((l, i) => (
        <div key={i} className="flex items-center gap-2 text-sm"><span className="flex-1 truncate">{l.name}</span>
          <input className="h-9 w-20 rounded-lg border border-slate-200 px-2 text-right" value={l.qty} onChange={(e) => set({ ...v, lines: v.lines.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)) })} data-testid={`admin-sale-qty-${i}`} /></div>
      ))}
      <div className="flex gap-2">
        <select className="h-10 rounded-lg border border-slate-200 px-2 text-sm" value={v.method} onChange={(e) => set({ ...v, method: e.target.value })} data-testid="admin-sale-method"><option>cash</option><option>card</option></select>
        {v.method === "cash" && <input className="h-10 flex-1 rounded-lg border border-slate-200 px-3 text-sm" value={v.tendered} onChange={(e) => set({ ...v, tendered: e.target.value })} placeholder="Cash tendered" data-testid="admin-sale-tendered" />}
      </div>
    </div>
  );
}

function EditDialog({ kind, rec, onClose, onSaved }) {
  const [v, setV] = useState(null);
  const [branches, setBranches] = useState([]);
  const [busy, setBusy] = useState(false);
  const [plans, setPlans] = useState([]);
  useEffect(() => { if (kind === "businesses") adminApi.get("/plans").then((r) => setPlans(r.data)); }, [kind]);
  useEffect(() => {
    if (!rec) return;
    const dp = rec.decimals ?? 2;
    setV(kind === "sales" ? { lines: rec.lines.map((l) => ({ ...l })), method: rec.payment?.method || "cash", tendered: rec.payment?.tendered != null ? (rec.payment.tendered / 10 ** dp).toFixed(dp) : "" } : { ...rec });
    const bid = kind === "businesses" ? rec.id : rec.business_id;
    adminApi.get("/data/branches", { params: { business_id: bid, size: 100 } }).then((r) => setBranches(r.data.rows));
  }, [rec, kind]);
  if (!rec || !v) return null;
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let body;
      if (kind === "stores") body = storeBody(v);
      else if (kind === "sales") body = { lines: v.lines.map((l) => ({ qty: parseInt(l.qty, 10) || 0, discount: l.discount_input || null })), payment: { method: v.method, tendered: v.tendered } };
      else {
        body = {};
        FIELDS[kind].forEach(([k, , t]) => {
          if (JSON.stringify(v[k]) === JSON.stringify(rec[k])) return;
          if (t === "int") body[k] = parseInt(v[k], 10);
          else if (k === "stock") body[k] = Object.fromEntries(Object.entries(v[k] || {}).map(([b, x]) => [b, x === "" || x == null ? null : parseInt(x, 10)]));
          else body[k] = v[k];
        });
        if (body.role || body.branch_ids) Object.assign(body, { role: v.role, branch_ids: v.branch_ids });
      }
      await adminApi.put(`/data/${kind}/${rec.id}`, body);
      toast.success("Saved");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={`max-h-[90dvh] overflow-y-auto ${kind === "stores" ? `sm:max-w-3xl max-sm:inset-0 max-sm:h-[100dvh] max-sm:max-h-none max-sm:w-full max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-none max-sm:left-0 max-sm:top-0` : "sm:max-w-lg"}`} data-testid="admin-edit-dialog">
        <DialogHeader><DialogTitle>Edit {recordName(kind, rec)}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          {kind === "stores" ? <StoreSettingsForm f={v} setF={setV} branches={branches} client={adminApi} uploadPath="/media/upload" uploadParams={{ business_id: rec.business_id }} />
            : kind === "sales" ? <SaleForm v={v} set={setV} /> : FIELDS[kind].map((f) => <Field key={f[0]} f={f} value={v[f[0]]} branches={branches} plans={plans} onChange={(x) => setV({ ...v, [f[0]]: x })} />)}
          {kind === "businesses" && <CustomPrices biz={rec} />}
          <button disabled={busy} className="h-11 w-full rounded-xl bg-slate-900 font-bold text-white disabled:opacity-50" data-testid="admin-edit-save">Save changes</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function NewStoreDialog({ open, onClose, bizList, onSaved }) {
  const [bid, setBid] = useState("");
  const [branches, setBranches] = useState([]);
  const [v, setV] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!open) { setBid(""); setV(null); } }, [open]);
  useEffect(() => {
    if (!bid) return;
    adminApi.get("/data/branches", { params: { business_id: bid, size: 100 } }).then((r) => {
      setBranches(r.data.rows);
      const b = bizList.find((x) => x.id === bid);
      setV({ ...emptyStore((b?.name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""), r.data.rows[0]?.id), name: b?.name || "" });
    });
  }, [bid, bizList]);
  if (!open) return null;
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await adminApi.post("/stores", { ...storeBody(v), business_id: bid }); toast.success("Store created"); onSaved(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-3xl max-sm:inset-0 max-sm:h-[100dvh] max-sm:max-h-none max-sm:w-full max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-none max-sm:left-0 max-sm:top-0" data-testid="admin-new-store-dialog">
        <DialogHeader><DialogTitle>New store</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <select value={bid} onChange={(e) => setBid(e.target.value)} className="h-10 w-full rounded-lg border border-slate-200 px-2 text-sm" data-testid="admin-new-store-business">
            <option value="">Choose a business</option>{bizList.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          {v && <StoreSettingsForm f={v} setF={setV} branches={branches} client={adminApi} uploadPath="/media/upload" uploadParams={{ business_id: bid }} />}
          <button disabled={busy || !v} className="h-11 w-full rounded-xl bg-slate-900 font-bold text-white disabled:opacity-50" data-testid="admin-new-store-save">Create store</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmDialog({ c, onClose }) {
  if (!c) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm" data-testid="admin-confirm-dialog">
        <DialogHeader><DialogTitle>{c.title}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">{c.text}</p>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="h-10 rounded-lg border border-slate-200 px-4 text-sm font-semibold">Cancel</button>
          <button onClick={c.go} className="h-10 rounded-lg bg-rose-600 px-4 text-sm font-bold text-white" data-testid="admin-confirm-ok">{c.ok}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminData() {
  const [kind, setKind] = useState("businesses");
  const [q, setQ] = useState("");
  const [biz, setBiz] = useState("");
  const [deleted, setDeleted] = useState(false);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ rows: [], total: 0, pages: 1, loading: true });
  const [bizList, setBizList] = useState([]);
  const [edit, setEdit] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [sel, setSel] = useState([]);
  const [newStore, setNewStore] = useState(false);

  const [dq, setDq] = useState("");
  const seq = useRef(0);
  useEffect(() => {
    const t = setTimeout(() => setDq(q), 300);
    return () => clearTimeout(t);
  }, [q]);
  // Only the latest request may update the table.
  const load = useCallback(() => {
    const n = ++seq.current;
    adminApi
      .get(`/data/${kind}`, { params: { q: dq, business_id: biz, deleted, page } })
      .then((r) => n === seq.current && setData(r.data))
      .catch((e) => n === seq.current && toast.error(errMsg(e)));
  }, [kind, dq, biz, deleted, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => setSel([]), [kind, dq, biz, deleted, page]);
  useEffect(() => { adminApi.get("/data/businesses", { params: { size: 100 } }).then((r) => setBizList(r.data.rows)); }, []);

  const act = (r, op) => {
    const name = recordName(kind, r);
    setConfirm({
      title: op === "delete" ? `Delete ${name}?` : `Restore ${name}?`,
      text: op === "delete" ? `${name} will be hidden from the business${kind === "sales" ? " and its quantities go back to stock" : ""}. You can restore it later.` : `${name} becomes visible again${kind === "sales" ? " and its quantities are deducted again" : ""}.`,
      ok: op === "delete" ? "Delete" : "Restore",
      go: async () => {
        try {
          if (op === "delete") await adminApi.delete(`/data/${kind}/${r.id}`);
          else await adminApi.post(`/data/${kind}/${r.id}/restore`);
          toast.success(op === "delete" ? `${name} deleted` : `${name} restored`);
          load();
        } catch (e) {
          toast.error(errMsg(e));
        } finally {
          setConfirm(null);
        }
      },
    });
  };
  const bulk = () => {
    const op = deleted ? "restore" : "delete";
    const what = countOf(sel.length, ONE[kind], kind);
    setConfirm({
      title: `${op === "delete" ? "Delete" : "Restore"} ${what}?`,
      text: op === "delete" ? `The ${what} will be hidden from their businesses${kind === "sales" ? " and their quantities go back to stock" : ""}. You can restore them later.` : `The ${what} become visible again${kind === "sales" ? " and their quantities are deducted again" : ""}.`,
      ok: op === "delete" ? `Delete ${sel.length}` : `Restore ${sel.length}`,
      go: async () => {
        try {
          const { data: r } = await adminApi.post(`/data/${kind}/bulk`, { action: op, ids: sel });
          (r.skipped ? toast.warning : toast.success)(bulkSummary(r, op === "delete" ? "deleted" : "restored"));
          setSel([]);
          load();
        } catch (e) {
          toast.error(errMsg(e));
        } finally {
          setConfirm(null);
        }
      },
    });
  };
  const pageIds = data.rows.map((r) => r.id);
  const allOn = pageIds.length > 0 && pageIds.every((id) => sel.includes(id));
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const logInAs = async (r) => {
    try { startImpersonation((await adminApi.post(`/businesses/${r.id}/impersonate`)).data.token); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-4" data-testid="admin-data">
      <div className="flex flex-wrap gap-1.5">
        {KINDS.map(([k, l]) => <button key={k} onClick={() => { setKind(k); setPage(1); setData({ rows: [], total: 0, pages: 1, loading: true }); }} data-testid={`admin-data-kind-${k}`} className={`h-9 rounded-full px-4 text-sm font-bold ${kind === k ? "bg-slate-900 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200"}`}>{l}</button>)}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search" className="h-10 w-56 rounded-lg border border-slate-200 bg-white px-3 text-sm" data-testid="admin-data-search" />
        <select value={biz} onChange={(e) => { setBiz(e.target.value); setPage(1); }} className="h-10 rounded-lg border border-slate-200 bg-white px-2 text-sm" data-testid="admin-data-business-filter">
          <option value="">All businesses</option>{bizList.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Switch checked={deleted} onCheckedChange={(x) => { setDeleted(x); setPage(1); }} data-testid="admin-data-show-deleted" />Show deleted</label>
        {kind === "stores" && <button onClick={() => setNewStore(true)} data-testid="admin-new-store" className="h-10 rounded-lg bg-slate-900 px-4 text-sm font-bold text-white">New store</button>}
        <span className="ml-auto font-mono text-xs text-slate-500" data-testid="admin-data-total">{data.total} rows</span>
      </div>
      {sel.length > 0 && (
        <div className="flex items-center gap-3 rounded-xl bg-slate-900 px-4 py-2.5 text-sm text-white" data-testid="admin-bulk-bar">
          <span className="font-semibold" data-testid="admin-bulk-count">{sel.length} selected</span>
          <button onClick={() => setSel([])} className="text-slate-300 underline-offset-4 hover:underline" data-testid="admin-bulk-clear">Clear</button>
          <button onClick={bulk} data-testid={deleted ? "admin-bulk-restore" : "admin-bulk-delete"} className={`ml-auto h-8 rounded-lg px-4 text-xs font-bold ${deleted ? "bg-emerald-500 text-slate-950" : "bg-rose-600 text-white"}`}>
            {deleted ? "Restore" : "Delete"} {sel.length}
          </button>
        </div>
      )}
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full min-w-[720px] text-left text-sm" data-testid="admin-data-table">
          <thead className="border-b border-slate-100 bg-slate-50">
            <tr>
              <th className="w-10 px-3 py-2"><input type="checkbox" checked={allOn} disabled={!pageIds.length} onChange={() => setSel(allOn ? sel.filter((id) => !pageIds.includes(id)) : [...new Set([...sel, ...pageIds])])} aria-label="Select all on this page" data-testid="admin-select-all" className="h-4 w-4 accent-slate-900" /></th>
              <th colSpan={6} className="px-3 py-2 text-left font-mono text-[10.5px] uppercase tracking-wider text-slate-500">Select all on this page</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.rows.map((r) => (
              <tr key={r.id} data-testid={`admin-row-${r.id}`} className={sel.includes(r.id) ? "bg-amber-50/60" : ""}>
                <td className="w-10 px-3 py-2.5"><input type="checkbox" checked={sel.includes(r.id)} onChange={() => toggle(r.id)} aria-label="Select row" data-testid={`admin-select-${r.id}`} className="h-4 w-4 accent-slate-900" /></td>
                {cols[kind](r).map((c, i) => <td key={i} className={`px-3 py-2.5 ${i === 0 ? "font-semibold text-slate-900" : "text-slate-600"}`}>{String(c ?? "—")}</td>)}
                <td className="whitespace-nowrap px-3 py-2 text-right">
                  {deleted ? (
                    <button onClick={() => act(r, "restore")} data-testid={`admin-restore-${r.id}`} className="h-8 rounded-lg bg-emerald-600 px-3 text-xs font-bold text-white">Restore</button>
                  ) : (
                    <div className="flex justify-end gap-1.5">
                      {kind === "businesses" && <button onClick={() => logInAs(r)} data-testid={`admin-login-as-${r.id}`} className="h-8 rounded-lg bg-amber-500 px-3 text-xs font-bold text-slate-950">Log in as</button>}
                      <button onClick={() => setEdit(r)} data-testid={`admin-edit-${r.id}`} className="h-8 rounded-lg border border-slate-200 px-3 text-xs font-bold">Edit</button>
                      <button onClick={() => act(r, "delete")} data-testid={`admin-delete-${r.id}`} className="h-8 rounded-lg border border-rose-200 px-3 text-xs font-bold text-rose-700">Delete</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {!data.rows.length && <tr><td className="px-3 py-8 text-center text-slate-400" data-testid="admin-data-empty">{data.loading ? "Loading…" : "Nothing found"}</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-end gap-2 text-sm">
        <button disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="admin-data-prev" className="h-9 rounded-lg border border-slate-200 bg-white px-3 disabled:opacity-40">Prev</button>
        <span className="font-mono text-xs" data-testid="admin-data-page">{page} / {data.pages}</span>
        <button disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid="admin-data-next" className="h-9 rounded-lg border border-slate-200 bg-white px-3 disabled:opacity-40">Next</button>
      </div>
      <EditDialog kind={kind} rec={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
      <ConfirmDialog c={confirm} onClose={() => setConfirm(null)} />
      <NewStoreDialog open={newStore} bizList={bizList} onClose={() => setNewStore(false)} onSaved={() => { setNewStore(false); load(); }} />
    </div>
  );
}
